import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import styles from './css/PopupCopyFeed.module.css';
import { FaTimes, FaCopy } from 'react-icons/fa';

const PopupCopyFeed = ({ token, feeds, onClose, onCopyComplete }) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1: Basic info, 2: ID column selection
  
  // Step 1 states
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [newClientName, setNewClientName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  
  // Custom data link states
  const [useCustomLink, setUseCustomLink] = useState(true); // Default to using custom link
  const [customDataLink, setCustomDataLink] = useState('');
  const [customDataType, setCustomDataType] = useState(null);
  const [customHeaderRow, setCustomHeaderRow] = useState(1);
  const [customXmlTag, setCustomXmlTag] = useState('item');
  const [linkValidationLoading, setLinkValidationLoading] = useState(false);
  const [linkValidationError, setLinkValidationError] = useState('');

  // Step 2 states
  const [feedData, setFeedData] = useState(null);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedOldIdColumn, setSelectedOldIdColumn] = useState(null);
  const [selectedNewIdColumn, setSelectedNewIdColumn] = useState(null);
  const [fetchingColumns, setFetchingColumns] = useState(false);
  const [columnsError, setColumnsError] = useState('');
  
  // Step 3 states (ID examples and confirmation)
  const [idExamples, setIdExamples] = useState([]);
  const [fetchingExamples, setFetchingExamples] = useState(false);
  const [examplesError, setExamplesError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [idMappingConfirmed, setIdMappingConfirmed] = useState(false);

  // Convert feeds to react-select format
  const feedOptions = feeds.map((feed) => ({
    value: feed.client_name,
    label: `${feed.client_name} (Credits: ${feed.credit_balance})`,
    feed: feed,
  }));

  // Data type options for custom link
  const dataTypeOptions = [
    { value: 'CSV', label: 'CSV' },
    { value: 'TSV', label: 'TSV' },
    { value: 'Excel', label: 'Excel' },
    { value: 'GoogleSheets', label: 'Google Sheets' },
    { value: 'JSON', label: 'JSON' },
    { value: 'XML', label: 'XML' },
  ];

  // Debounced name validation
  useEffect(() => {
    if (!newClientName) {
      setNameError('');
      return;
    }

    const timer = setTimeout(() => {
      checkClientName(newClientName);
    }, 500);

    return () => clearTimeout(timer);
  }, [newClientName]);

  const checkClientName = async (clientName) => {
    if (!clientName.trim()) {
      setNameError('');
      return;
    }

    setNameCheckLoading(true);
    setNameError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/check-client-name`,
        { clientName: clientName.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.isTaken) {
        setNameError('This client name is already taken.');
      } else {
        setNameError('');
      }
    } catch (err) {
      console.error('Failed to check client name:', err);
      setNameError(
        err.response?.data?.message || 'Failed to validate client name.'
      );
    } finally {
      setNameCheckLoading(false);
    }
  };

  const handleNext = async () => {
    setError('');
    setLoading(true);
    
    // Validation
    if (useCustomLink) {
      if (!customDataLink.trim()) {
        setError('Please provide a custom data link.');
        setLoading(false);
        return;
      }
      if (!customDataType) {
        setError('Please select a data type for the custom link.');
        setLoading(false);
        return;
      }
    } else {
      if (!selectedFeed) {
        setError('Please select a feed to copy from.');
        setLoading(false);
        return;
      }
    }
    
    try {
      let feedInfo;
      
      if (useCustomLink) {
        // Create custom feed info object
        feedInfo = {
          data_type: customDataType.value,
          data_link: customDataLink.trim(),
          header_row: ['CSV', 'TSV', 'Excel', 'GoogleSheets'].includes(customDataType.value) ? customHeaderRow : null,
          xml_item_tag: customDataType.value === 'XML' ? customXmlTag : null,
          id_field: ['XML', 'JSON'].includes(customDataType.value) ? null : null, // Will be set later
          id_column: ['CSV', 'TSV', 'Excel', 'GoogleSheets'].includes(customDataType.value) ? null : null, // Will be set later
        };
      } else {
        // Fetch original feed data
        const feedResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-feed`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { client_name: selectedFeed.value },
          }
        );
        feedInfo = feedResponse.data;
      }
      
      setFeedData(feedInfo);
      
      // Fetch available columns based on data type
      await fetchAvailableColumns(feedInfo);
      
      // Move to next step
      setCurrentStep(2);
    } catch (err) {
      console.error('Failed to fetch feed data:', err);
      setError(
        err.response?.data?.error || 'Failed to fetch feed information.'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableColumns = async (feedInfo) => {
    setFetchingColumns(true);
    setColumnsError('');
    
    try {
      const dataType = feedInfo.data_type;
      const dataLink = feedInfo.data_link;
      
      if (!dataLink) {
        setColumnsError('No data link found for this feed.');
        return;
      }
      
      let response;
      let columns = [];
      
      if (dataType === 'CSV' || dataType === 'TSV' || dataType === 'Excel' || dataType === 'GoogleSheets') {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/extract-csv-columns`,
          {
            link: dataLink,
            headerRow: feedInfo.header_row || 1,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        columns = response.data.columns || [];
      } else if (dataType === 'JSON') {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/extract-json-fields`,
          {
            link: dataLink,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        columns = response.data.uniqueFields || [];
      } else if (dataType === 'XML') {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/extract-xml-subtags`,
          {
            link: dataLink,
            tag: feedInfo.xml_item_tag || 'item',
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        columns = response.data.uniqueSubTags || [];
      } else {
        setColumnsError(`Data type "${dataType}" is not supported for column extraction.`);
        return;
      }
      
      // Convert to select options
      const columnOptions = columns.map(col => ({
        value: col,
        label: col,
      }));
      
      setAvailableColumns(columnOptions);
      
      // Pre-select current ID column if it exists
      if (feedInfo.id_column) {
        const currentIdOption = columnOptions.find(opt => opt.value === feedInfo.id_column);
        if (currentIdOption) {
          setSelectedOldIdColumn(currentIdOption);
          setSelectedNewIdColumn(currentIdOption); // Default to same column
        }
      } else if (feedInfo.id_field) {
        const currentIdOption = columnOptions.find(opt => opt.value === feedInfo.id_field);
        if (currentIdOption) {
          setSelectedOldIdColumn(currentIdOption);
          setSelectedNewIdColumn(currentIdOption); // Default to same column
        }
      }
      
    } catch (err) {
      console.error('Failed to fetch columns:', err);
      setColumnsError(
        err.response?.data?.message || 'Failed to fetch available columns.'
      );
    } finally {
      setFetchingColumns(false);
    }
  };

  // Fetch ID examples when both columns are selected
  useEffect(() => {
    if (selectedOldIdColumn && selectedNewIdColumn && feedData) {
      fetchIdExamples();
    } else {
      setIdExamples([]);
      setShowConfirmation(false);
      setIdMappingConfirmed(false); // Reset confirmation when columns change
    }
  }, [selectedOldIdColumn, selectedNewIdColumn, feedData]);

  const fetchIdExamples = async () => {
    setFetchingExamples(true);
    setExamplesError('');
    setShowConfirmation(false);
    
    try {
      // Prepare the payload for the new sample data endpoint
      const payload = {
        link: feedData.data_link,
        data_type: feedData.data_type,
        sample_size: 5,
      };
      
      // Add data type specific configuration
      if (feedData.data_type === 'CSV' || feedData.data_type === 'TSV' || feedData.data_type === 'Excel' || feedData.data_type === 'GoogleSheets') {
        payload.header_row = feedData.header_row || 1;
      } else if (feedData.data_type === 'XML') {
        payload.xml_item_tag = feedData.xml_item_tag || 'item';
      }
      
      // Fetch sample data using the new dedicated endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/get-sample-data`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      const sampleData = response.data.sample_data || [];
      
      // Generate ID examples from the sample data
      const examples = sampleData.map((row, index) => {
        const oldId = row[selectedOldIdColumn.value] || `missing_${index + 1}`;
        // Extract the new ID from the selected new ID column
        const newId = row[selectedNewIdColumn.value] || `missing_${index + 1}`;
        
        return {
          old_id: oldId,
          new_id: newId,
          preview_data: row
        };
      });
      
      setIdExamples(examples);
      setShowConfirmation(true);
      
    } catch (err) {
      console.error('Failed to fetch sample data:', err);
      
      // Fallback: Create realistic mock examples based on column names
      try {
        const mockExamples = Array.from({ length: 5 }, (_, i) => {
          // Create more realistic mock data based on column names
          const oldIdValue = selectedOldIdColumn.value.toLowerCase().includes('id') 
            ? `${Math.floor(Math.random() * 10000) + 1}`
            : selectedOldIdColumn.value.toLowerCase().includes('sku')
            ? `SKU${String(Math.floor(Math.random() * 10000) + 1).padStart(6, '0')}`
            : selectedOldIdColumn.value.toLowerCase().includes('code')
            ? `CODE_${String(Math.floor(Math.random() * 1000) + 1).padStart(4, '0')}`
            : `${selectedOldIdColumn.value}_${i + 1}`;
            
          const newIdValue = selectedNewIdColumn.value.toLowerCase().includes('id') 
            ? `${Math.floor(Math.random() * 10000) + 1}`
            : selectedNewIdColumn.value.toLowerCase().includes('sku')
            ? `SKU${String(Math.floor(Math.random() * 10000) + 1).padStart(6, '0')}`
            : selectedNewIdColumn.value.toLowerCase().includes('code')
            ? `CODE_${String(Math.floor(Math.random() * 1000) + 1).padStart(4, '0')}`
            : `${selectedNewIdColumn.value}_${i + 1}`;
          
          return {
            old_id: oldIdValue,
            new_id: newIdValue,
            preview_data: {
              [selectedOldIdColumn.value]: oldIdValue,
              [selectedNewIdColumn.value]: newIdValue,
              // Add some sample context data
              'name': `Sample Product ${i + 1}`,
              'description': `This is sample data for item ${i + 1}`,
            }
          };
        });
        
        setIdExamples(mockExamples);
        setShowConfirmation(true);
        
        // Show a warning that this is sample data
        setExamplesError(
          err.response?.data?.message || 
          'Note: Could not fetch real data. Showing sample structure. Actual data will be used during the copy process.'
        );
      } catch (mockErr) {
        setExamplesError(
          'Failed to fetch sample data from the data source. Please verify the data source is accessible.'
        );
      }
    } finally {
      setFetchingExamples(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!selectedOldIdColumn || !selectedNewIdColumn) {
      setError('Please select both old and new ID columns.');
      return;
    }

    if (!showConfirmation) {
      setError('Please wait for ID examples to load and confirm the mapping.');
      return;
    }

    if (!idMappingConfirmed) {
      setError('Please confirm that you have checked the ID mapping is correct.');
      return;
    }

    setLoading(true);
    try {
      // TODO: Implement the actual copy feed API endpoint
      // For now, this is a placeholder
      const requestPayload = {
        source_client_name: selectedFeed.value,
        new_client_name: newClientName.trim(),
        old_id_column: selectedOldIdColumn.value,
        new_id_column: selectedNewIdColumn.value,
      };

      // Add custom link information if used
      if (useCustomLink) {
        requestPayload.custom_data_source = {
          data_link: customDataLink.trim(),
          data_type: customDataType.value,
          header_row: customHeaderRow,
          xml_item_tag: customXmlTag,
        };
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/copy-feed`,
        requestPayload,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob', // Important for file download
        }
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `copy_feed_results_${newClientName.trim()}_${new Date().toISOString().slice(0, 10)}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Feed copied successfully! CSV report downloaded. You can now safely close this window.');

      // Trigger parent refresh if callback provided
      if (onCopyComplete) {
        onCopyComplete();
      }

      // Close the popup after a short delay
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      console.error('Failed to copy feed:', err);
      
      // Handle blob response errors
      if (err.response && err.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            setError(errorData.message || 'Failed to copy feed. Please try again.');
          } catch {
            setError('Failed to copy feed. Please try again.');
          }
        };
        reader.readAsText(err.response.data);
      } else {
        setError(
          err.response?.data?.message || 'Failed to copy feed. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    setError('');
    setColumnsError('');
    setIdMappingConfirmed(false); // Reset confirmation when going back
  };

  // Validate custom link
  const validateCustomLink = async () => {
    if (!customDataLink.trim() || !customDataType) return;
    
    setLinkValidationLoading(true);
    setLinkValidationError('');
    
    try {
      let endpoint;
      let payload = { link: customDataLink.trim() };
      
      if (customDataType.value === 'CSV' || customDataType.value === 'TSV' || customDataType.value === 'Excel' || customDataType.value === 'GoogleSheets') {
        endpoint = '/extract-csv-columns';
        payload.headerRow = customHeaderRow;
      } else if (customDataType.value === 'JSON') {
        endpoint = '/extract-json-fields';
      } else if (customDataType.value === 'XML') {
        endpoint = '/extract-xml-subtags';
        payload.tag = customXmlTag;
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // If we get here, the link is valid
      setLinkValidationError('');
    } catch (err) {
      console.error('Link validation failed:', err);
      setLinkValidationError(
        err.response?.data?.message || 'Failed to validate the provided link.'
      );
    } finally {
      setLinkValidationLoading(false);
    }
  };

  // Debounced link validation
  useEffect(() => {
    if (useCustomLink && customDataLink && customDataType) {
      const timer = setTimeout(() => {
        validateCustomLink();
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setLinkValidationError('');
    }
  }, [customDataLink, customDataType, customHeaderRow, customXmlTag, useCustomLink]);

  // Prevent page unload during copying
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = 'Feed copying is in progress. Are you sure you want to leave?';
        return 'Feed copying is in progress. Are you sure you want to leave?';
      }
    };

    if (loading) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [loading]);

  const getInputClassName = () => {
    let className = styles.input;
    if (nameCheckLoading) {
      className += ` ${styles.inputLoading}`;
    } else if (nameError) {
      className += ` ${styles.inputError}`;
    } else if (newClientName && !nameError) {
      className += ` ${styles.inputSuccess}`;
    }
    return className;
  };

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        <button
          className={styles.closeButton}
          onClick={() => {
            if (loading) {
              if (window.confirm('Copying is in progress. Are you sure you want to close? This may interrupt the process.')) {
                onClose();
              }
            } else {
              onClose();
            }
          }}
          aria-label="Close"
        >
          <FaTimes size={20} />
        </button>
        
        <div className={styles.header}>
          <FaCopy className={styles.headerIcon} />
          <h2 id="copy-feed-title">Copy Feed With New IDs - Step {currentStep} of 2</h2>
        </div>

        {currentStep === 1 ? (
          <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="selectedFeed">Select Feed to Copy:</label>
            <Select
              id="selectedFeed"
              options={feedOptions}
              value={selectedFeed}
              onChange={setSelectedFeed}
              className={styles.selectInput}
              classNamePrefix="react-select"
              placeholder="Choose a feed..."
              isSearchable
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="newClientName">New Client Name:</label>
            <div className={styles.inputContainer}>
              <input
                type="text"
                id="newClientName"
                name="newClientName"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                className={getInputClassName()}
                placeholder="Enter new client name..."
                required
                aria-required="true"
              />
              {nameCheckLoading && (
                <div className={styles.loadingSpinner}></div>
              )}
            </div>
            {nameError && <p className={styles.fieldError}>{nameError}</p>}
            {newClientName && !nameError && !nameCheckLoading && (
              <p className={styles.fieldSuccess}>✓ Name is available</p>
            )}
          </div>

          {/* Data Source Option */}
          <div className={styles.field}>
            <div className={styles.checkboxContainer}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={!useCustomLink}
                  onChange={(e) => setUseCustomLink(!e.target.checked)}
                  className={styles.checkbox}
                />
                Use the same data source as the original feed
              </label>
            </div>
          </div>

          {useCustomLink && (
            <div className={styles.customLinkSection}>
              <h4>New Data Source</h4>
              <div className={styles.field}>
                <label htmlFor="customDataLink">Data Source Link:</label>
                <input
                  type="url"
                  id="customDataLink"
                  value={customDataLink}
                  onChange={(e) => setCustomDataLink(e.target.value)}
                  className={styles.input}
                  placeholder="Enter data source URL..."
                  required
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="customDataType">Data Type:</label>
                <Select
                  id="customDataType"
                  options={dataTypeOptions}
                  value={customDataType}
                  onChange={setCustomDataType}
                  className={styles.selectInput}
                  classNamePrefix="react-select"
                  placeholder="Select data type..."
                  required
                />
              </div>

              {customDataType && ['CSV', 'TSV', 'Excel', 'GoogleSheets'].includes(customDataType.value) && (
                <div className={styles.field}>
                  <label htmlFor="customHeaderRow">Header Row Number:</label>
                  <input
                    type="number"
                    id="customHeaderRow"
                    value={customHeaderRow}
                    onChange={(e) => setCustomHeaderRow(parseInt(e.target.value) || 1)}
                    className={styles.input}
                    min="1"
                    required
                  />
                  <p className={styles.fieldHint}>Row number where column headers are located</p>
                </div>
              )}

              {customDataType && customDataType.value === 'XML' && (
                <div className={styles.field}>
                  <label htmlFor="customXmlTag">XML Item Tag:</label>
                  <input
                    type="text"
                    id="customXmlTag"
                    value={customXmlTag}
                    onChange={(e) => setCustomXmlTag(e.target.value)}
                    className={styles.input}
                    placeholder="item"
                    required
                  />
                  <p className={styles.fieldHint}>XML tag that contains individual items</p>
                </div>
              )}

              {/* Link Validation Feedback */}
              {linkValidationLoading && (
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingSpinner}></div>
                  <span>Validating data source...</span>
                </div>
              )}
              {linkValidationError && (
                <p className={styles.fieldError}>{linkValidationError}</p>
              )}
              {!linkValidationLoading && !linkValidationError && customDataLink && customDataType && (
                <p className={styles.fieldSuccess}>✓ Data source is accessible</p>
              )}
            </div>
          )}

          {selectedFeed && (
            <div className={styles.previewSection}>
              <h3>Copy Details:</h3>
              <div className={styles.copyPreview}>
                <div className={styles.previewItem}>
                  <strong>Source:</strong> {selectedFeed.feed.client_name}
                </div>
                <div className={styles.previewItem}>
                  <strong>New Name:</strong> {newClientName || 'Not specified'}
                </div>
                                  <div className={styles.previewItem}>
                    <strong>Data Source:</strong> {useCustomLink ? 'Custom data source' : 'Same as original feed'}
                  </div>
                {useCustomLink && customDataType && (
                  <div className={styles.previewItem}>
                    <strong>Custom Data Type:</strong> {customDataType.label}
                  </div>
                )}
                <div className={styles.previewItem}>
                  <strong>Credits:</strong> Will be set to 0 (default)
                </div>
                <div className={styles.previewItem}>
                  <strong>Action:</strong> All data will be copied with new IDs
                </div>
              </div>
            </div>
          )}

            {error && <p className={styles.error}>{error}</p>}
            {success && <p className={styles.success}>{success}</p>}

            <div className={styles.buttons}>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={
                  loading || 
                  nameCheckLoading || 
                  linkValidationLoading ||
                  !!nameError || 
                  !!linkValidationError ||
                  !newClientName.trim() ||
                  (useCustomLink && (!customDataLink.trim() || !customDataType)) ||
                  (!useCustomLink && !selectedFeed)
                }
              >
                {loading ? 'Loading...' : 'Next'}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => {
                  if (loading) {
                    if (window.confirm('Copying is in progress. Are you sure you want to cancel? This may interrupt the process.')) {
                      onClose();
                    }
                  } else {
                    onClose();
                  }
                }}
                disabled={loading}
                title={loading ? 'Cannot cancel while copying is in progress' : 'Cancel operation'}
              >
                {loading ? 'Please Wait...' : 'Cancel'}
              </button>
            </div>
          </form>
        ) : (
          <div className={styles.form}>
            <div className={styles.stepHeader}>
              <h3>Select ID Column</h3>
              <p>Choose the column that contains the unique identifiers for your feed data.</p>
            </div>

            {feedData && (
              <div className={styles.feedInfoSection}>
                <h4>Feed Information:</h4>
                <div className={styles.feedInfo}>
                  <div className={styles.infoItem}>
                    <strong>Data Type:</strong> {feedData.data_type}
                  </div>
                  <div className={styles.infoItem}>
                    <strong>Data Source:</strong> {feedData.data_link}
                  </div>
                  {feedData.id_column && (
                    <div className={styles.infoItem}>
                      <strong>Current ID Column:</strong> {feedData.id_column}
                    </div>
                  )}
                  {feedData.id_field && (
                    <div className={styles.infoItem}>
                      <strong>Current ID Field:</strong> {feedData.id_field}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={styles.field}>
              <label htmlFor="oldIdColumn">Old ID Column:</label>
              {fetchingColumns ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingSpinner}></div>
                  <span>Fetching available columns...</span>
                </div>
              ) : columnsError ? (
                <div className={styles.errorContainer}>
                  <p className={styles.fieldError}>{columnsError}</p>
                  <button
                    type="button"
                    onClick={() => fetchAvailableColumns(feedData)}
                    className={styles.retryButton}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <Select
                  id="oldIdColumn"
                  options={availableColumns}
                  value={selectedOldIdColumn}
                  onChange={setSelectedOldIdColumn}
                  className={styles.selectInput}
                  classNamePrefix="react-select"
                  placeholder="Select old ID column..."
                  isSearchable
                  required
                />
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="newIdColumn">New ID Column:</label>
              <Select
                id="newIdColumn"
                options={availableColumns}
                value={selectedNewIdColumn}
                onChange={setSelectedNewIdColumn}
                className={styles.selectInput}
                classNamePrefix="react-select"
                placeholder="Select new ID column..."
                isSearchable
                required
                isDisabled={fetchingColumns || !!columnsError}
              />

            </div>

            {/* ID Examples Section */}
            {selectedOldIdColumn && selectedNewIdColumn && (
              <div className={styles.examplesSection}>
                <h3>ID Mapping Examples</h3>
                <p className={styles.examplesDescription}>
                  Please review these examples to confirm the ID mapping is correct:
                </p>
                
                {fetchingExamples ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Generating ID examples...</span>
                  </div>
                ) : examplesError ? (
                  <div className={styles.errorContainer}>
                    <p className={styles.fieldError}>{examplesError}</p>
                    <button
                      type="button"
                      onClick={fetchIdExamples}
                      className={styles.retryButton}
                    >
                      Retry
                    </button>
                  </div>
                ) : idExamples.length > 0 ? (
                  <div className={styles.examplesTable}>
                    <div className={styles.examplesHeader}>
                      <div className={styles.exampleColumn}>
                        <strong>Old ID ({selectedOldIdColumn.value})</strong>
                      </div>
                      <div className={styles.exampleArrow}>→</div>
                      <div className={styles.exampleColumn}>
                        <strong>New ID ({selectedNewIdColumn.value})</strong>
                      </div>
                    </div>
                    {idExamples.map((example, index) => (
                      <div key={index} className={styles.exampleRow}>
                        <div className={styles.exampleColumn}>
                          <code>{example.old_id}</code>
                        </div>
                        <div className={styles.exampleArrow}>→</div>
                        <div className={styles.exampleColumn}>
                          <code>{example.new_id}</code>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                
                {showConfirmation && (
                  <div className={styles.confirmationSection}>
                    <div className={styles.warningBox}>
                      <strong>⚠️ Important:</strong> Please confirm that this ID mapping is correct. 
                      All data will be copied with these new IDs and cannot be easily undone.
                    </div>
                    <div className={styles.confirmationCheckbox}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={idMappingConfirmed}
                          onChange={(e) => setIdMappingConfirmed(e.target.checked)}
                          className={styles.checkbox}
                        />
                        <strong>I have checked the ID mapping is correct and understand this action cannot be easily undone</strong>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedOldIdColumn && selectedNewIdColumn && showConfirmation && (
              <div className={styles.previewSection}>
                <h3>Copy Configuration:</h3>
                <div className={styles.copyPreview}>
                  <div className={styles.previewItem}>
                    <strong>Source Feed:</strong> {selectedFeed.feed.client_name}
                  </div>
                  <div className={styles.previewItem}>
                    <strong>New Feed Name:</strong> {newClientName}
                  </div>
                  <div className={styles.previewItem}>
                    <strong>Old ID Column:</strong> {selectedOldIdColumn.value}
                  </div>
                  <div className={styles.previewItem}>
                    <strong>New ID Column:</strong> {selectedNewIdColumn.value}
                  </div>
                  <div className={styles.previewItem}>
                    <strong>Action:</strong> All data will be copied with new unique IDs
                  </div>
                </div>
                
                <div className={styles.processingWarning} style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  padding: '15px',
                  marginTop: '15px'
                }}>
                  <h4>⚠️ Important Processing Information:</h4>
                  <ul>
                    <li><strong>Processing Time:</strong> Approximately 1 minute per 1,000 products
                      {idExamples.length > 0 && (
                        <div style={{marginTop: '5px', fontSize: '0.9em', color: '#666'}}>
                          Based on sample data, this may take several minutes to complete
                        </div>
                      )}
                    </li>
                    <li><strong>Do NOT close this window</strong> until the process completes</li>
                    <li><strong>Do NOT navigate away</strong> from this page during copying</li>
                    <li>A CSV report will automatically download when complete</li>
                    <li>The process cannot be easily undone once started</li>
                  </ul>
                </div>
              </div>
            )}

            {error && <p className={styles.error}>{error}</p>}
            {success && <p className={styles.success}>{success}</p>}

            <div className={styles.buttons}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBack}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="button"
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={loading || fetchingColumns || fetchingExamples || !selectedOldIdColumn || !selectedNewIdColumn || !showConfirmation || !idMappingConfirmed}
              >
                {loading ? (
                  <span>
                    🔄 Copying Feed... 
                    <br />
                    <small>Please do not close this window</small>
                  </span>
                ) : 'Copy Feed'}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PopupCopyFeed; 